import { createApi } from '@reduxjs/toolkit/query/react';
import {
  FilterPartnerLandingsResponse,
  GetLandingLayoutsResponse,
  GetPartnerLandingByIdResponse,
  GetPartnerLandingTemplateResponse,
  LandingContentByIdResponse,
  LandingTypesResponse,
  PartnerLandingRequest,
  PatchPartnerLandingRequest,
} from './models';
import { baseQueryWithReauth } from '../../main/baseQueryWithReauth';
const partner_landings = `partner_landings`;

export const partnerLandings = createApi({
  reducerPath: partner_landings,
  baseQuery: baseQueryWithReauth,
  tagTypes: [partner_landings],
  //feature
  // refetchOnFocus: true,
  endpoints: (build) => ({
    getFilterPartnerLandings: build.query<
      FilterPartnerLandingsResponse,
      { search?: string; page?: number }
    >({
      query: ({ search, page }) => {
        const params = new URLSearchParams();

        if (search && !(search.length > 0 && search.length < 3)) {
          params.append('search', search.toString());
        }
        if (page) {
          params.append('page', page.toString());
        }
        const queryString = params.toString();

        return {
          url: `/partner/landings/filter/${queryString ? '?' + queryString : ''}`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      providesTags: [{ type: partner_landings, id: 'LIST' }],
    }),
    getPartnerLandingTypes: build.query<LandingTypesResponse, void>({
      query: () => {
        return {
          url: '/partner/landing-types',
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
    }),
    getPartnerLandingContentById: build.query<LandingContentByIdResponse, { id: string }>({
      query: ({ id }) => {
        return {
          url: `/partner/landing-types/${id}/content`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
    }),

    //DETAILS or CARDS
    deletePartnerLandingById: build.mutation<any, { landing: string }>({
      query: ({ landing }) => ({
        method: 'DELETE',
        url: `/partner/landings/${landing}`,
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
      invalidatesTags: [{ type: partner_landings, id: 'LIST' }],
    }),

    copyLandingToEvent: build.mutation<
      GetPartnerLandingByIdResponse,
      { landingId: string; eventId?: string }
    >({
      query: ({ landingId, eventId }) => ({
        method: 'POST',
        url: `/partner/landings/${landingId}/copy`,
        headers: {
          Accept: 'application/json',
        },
        body: { event_id: eventId },
      }),
      invalidatesTags: [{ type: partner_landings, id: 'LIST' }],
    }),

    // WYSIWYG EDITOR
    publishLanding: build.mutation<void, { landingId: string; status: number; transition: number }>(
      {
        query: ({ landingId, status, transition }) => ({
          method: 'PATCH',
          url: `/partner/landings/${landingId}/status/${status}`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
          body: { transition },
        }),
        invalidatesTags: (_result, _error, { landingId }) => [
          { type: partner_landings, id: 'LIST' },
          { type: partner_landings, id: landingId },
        ],
      },
    ),

    // for 'Add section' modal
    getLandingsLayoutsSections: build.query<GetLandingLayoutsResponse, void>({
      query: () => {
        return {
          url: `/partner/landings/sections`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
    }),

    // create new landing page
    postPartnerCreateLanding: build.mutation<GetPartnerLandingByIdResponse, PartnerLandingRequest>({
      query: ({ eventId, body }) => ({
        method: 'POST',
        url: `/partner/landings`,
        headers: {
          Accept: 'application/json',
        },
        body: { ...body, event_id: eventId },
      }),
      invalidatesTags: [{ type: partner_landings, id: 'LIST' }],
    }),

    // edit or update landing page
    patchPartnerLandingById: build.mutation<
      GetPartnerLandingByIdResponse,
      PatchPartnerLandingRequest
    >({
      query: ({ landingId, body }) => ({
        method: 'PATCH',
        url: `/partner/landings/${landingId}`,
        headers: {
          Accept: 'application/json',
        },
        body,
      }),
      invalidatesTags: [{ type: partner_landings, id: 'LIST' }],
      async onQueryStarted({ landingId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            partnerLandings.util.updateQueryData(
              'getPartnerLandingById',
              { landingId },
              () => data,
            ),
          );
        } catch {
          /* mutation failed — cache stays as is */
        }
      },
    }),

    // fetch all data for editor
    getPartnerLandingById: build.query<GetPartnerLandingByIdResponse, { landingId: string }>({
      query: ({ landingId }) => ({
        url: `/partner/landings/${landingId}`,
      }),
      providesTags: (_result, _error, { landingId }) => [{ type: partner_landings, id: landingId }],
    }),

    getPartnerLandingTemplateById: build.query<
      GetPartnerLandingTemplateResponse,
      { eventId: string }
    >({
      query: ({ eventId }) => ({
        url: `/partner/landing-template?event_id=${eventId}`,
      }),
    }),
  }),
});

//Lazy for clicks
export const {
  useGetFilterPartnerLandingsQuery, //TODO: check if BE is ready
  // new
  useDeletePartnerLandingByIdMutation,
  usePublishLandingMutation, //TODO: check if BE is ready

  // EDITOR
  useGetLandingsLayoutsSectionsQuery,
  usePostPartnerCreateLandingMutation,
  usePatchPartnerLandingByIdMutation,
  useGetPartnerLandingByIdQuery,
  useGetPartnerLandingTypesQuery,
  useGetPartnerLandingContentByIdQuery,
  useGetPartnerLandingTemplateByIdQuery,
  useCopyLandingToEventMutation,
} = partnerLandings;
